<template>
  <div class="lecture-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Lectures</h1>
        <p>Manage course lectures and learning content.</p>
      </div>

      <button class="primary-btn" @click="openForm()">
        <i class="bx bx-plus"></i>
        Add Lecture
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-card">

      <div class="search-box">
        <i class="bx bx-search"></i>

        <input
          v-model="search"
          type="text"
          placeholder="Search lectures..."
        />
      </div>

      <!-- Course Filter -->
      <select v-model="selectedCourse">
        <option value="">All Courses</option>

        <option
          v-for="course in courses"
          :key="course.course_id"
          :value="course.course_id"
        >
          {{ course.title }}
        </option>
      </select>

      <!-- Module Filter -->
      <select v-model="selectedModule">
        <option value="">All Modules</option>

        <option
          v-for="module in filteredModulesForDropdown"
          :key="module.module_id"
          :value="module.module_id"
        >
          {{ module.module_title }}
        </option>
      </select>

      <!-- Category Filter -->
      <select v-model="selectedCategory">
        <option value="">All Categories</option>

        <option
          v-for="category in categories"
          :key="category.cat_id"
          :value="category.cat_id"
        >
          {{ category.cat_name }}
        </option>
      </select>

      <button class="reset-btn" @click="resetFilters">
        <i class="bx bx-reset"></i>
        Reset
      </button>

    </div>

    <!-- Table -->
    <div class="table-card">

      <div class="table-top">
        <div>
          <h3>Lecture List</h3>
          <span>{{ totalLectures }} lectures</span>
        </div>
      </div>

      <div class="table-wrapper">

        <table>

          <thead>
            <tr>
              <th>Lecture</th>
              <th>Course</th>
              <th>Module</th>
              <th>Category</th>
              <th>Duration</th>
              <th>Video</th>
              <th>Created</th>
              <th class="action-col">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr
              v-for="lecture in lectures"
              :key="lecture.lession_id"
            >

              <!-- Lecture -->
              <td>
                <div class="lecture-info">

                  <div class="lecture-icon">
                    <i class="bx bx-play-circle"></i>
                  </div>

                  <div>
                    <strong>
                      {{ lecture.lession_title }}
                    </strong>

                    <small>
                      #{{ lecture.lession_id }}
                    </small>
                  </div>

                </div>
              </td>

              <!-- Course -->
              <td>
                <span class="course-name">
                  {{ getCourseName(lecture.lession_parent) }}
                </span>
              </td>

              <!-- Module -->
              <td>
                {{ getModuleName(lecture.lession_module) }}
              </td>

              <!-- Category -->
              <td>
                <span class="category-badge">
                  {{ getCategoryName(lecture.lession_cat) }}
                </span>
              </td>

              <!-- Duration -->
              <td>
                {{ lecture.lession_duration || '-' }}

                <span
                  v-if="lecture.lession_duration"
                  class="duration-label"
                >
                  min
                </span>
              </td>

              <!-- Video -->
              <td>

                <span
                  v-if="lecture.lession_video"
                  class="video-badge"
                >
                  <i class="bx bx-video"></i>
                  Available
                </span>

                <span
                  v-else
                  class="no-video"
                >
                  No video
                </span>

              </td>

              <!-- Created -->
              <td>
                {{ formatDate(lecture.lession_datetime) }}
              </td>

              <!-- Actions -->
              <td>

                <div class="actions">

                  <!-- View -->
                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewLecture(lecture)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <!-- Edit -->
                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="openForm(lecture)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <!-- Delete -->
                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="openDelete(lecture)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>

              </td>

            </tr>

            <!-- Empty State -->
            <tr v-if="!loading && lectures.length === 0">
              <td colspan="8">

                <div class="empty-state">
                  <i class="bx bx-book-open"></i>

                  <h4>No lectures found</h4>

                  <p>
                    Try changing your search or filters.
                  </p>
                </div>

              </td>
            </tr>

            <!-- Loading -->
            <tr v-if="loading">
              <td colspan="8">

                <div class="empty-state">
                  <i class="bx bx-loader-alt bx-spin"></i>

                  <h4>Loading lectures...</h4>
                </div>

              </td>
            </tr>

          </tbody>

        </table>

      </div>

      <!-- Pagination -->
      <div
        v-if="totalLectures > 0"
        class="pagination"
      >

        <span>
          Showing
          {{ startIndex + 1 }}
          -
          {{ Math.min(endIndex, totalLectures) }}
          of
          {{ totalLectures }}
        </span>

        <div class="pagination-buttons">

          <!-- Previous -->
          <button
            :disabled="page === 0"
            @click="prevPage"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <!-- Pages -->
          <button
            v-for="pageNumber in totalPages"
            :key="pageNumber"
            :class="{ active: page + 1 === pageNumber }"
            @click="
              page = pageNumber - 1;
              loadLectures();
            "
          >
            {{ pageNumber }}
          </button>

          <!-- Next -->
          <button
            :disabled="page >= totalPages - 1"
            @click="nextPage"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- ========================================================= -->
    <!-- ADD / EDIT MODAL -->
    <!-- ========================================================= -->

    <div
      v-if="showModal"
      class="modal-overlay"
      @click.self="close"
    >

      <div class="modal">

        <!-- Header -->
        <div class="modal-header">

          <div>

            <h3>
              {{ form.lession_id ? 'Edit Lecture' : 'Add Lecture' }}
            </h3>

            <p>
              {{
                form.lession_id
                  ? 'Update lecture information.'
                  : 'Create a new course lecture.'
              }}
            </p>

          </div>

          <button
            class="close-btn"
            @click="close"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <!-- Body -->
        <div class="modal-body">

          <div class="form-grid">

            <!-- Course -->
            <div class="form-group">

              <label>
                Course <span>*</span>
              </label>

              <select
                v-model="form.lession_parent"
                @change="onFormCourseChange"
              >

                <option value="">
                  Select Course
                </option>

                <option
                  v-for="course in courses"
                  :key="course.course_id"
                  :value="course.course_id"
                >
                  {{ course.title }}
                </option>

              </select>

            </div>

            <!-- Module -->
            <div class="form-group">

              <label>Module</label>

              <select
                v-model="form.lession_module"
              >

                <option value="">
                  Select Module
                </option>

                <option
                  v-for="module in formModules"
                  :key="module.module_id"
                  :value="module.module_id"
                >
                  {{ module.module_title }}
                </option>

              </select>

            </div>

            <!-- Lecture Title -->
            <div class="form-group full">

              <label>
                Lecture Title <span>*</span>
              </label>

              <input
                v-model="form.lession_title"
                type="text"
                placeholder="Enter lecture title"
              />

            </div>

            <!-- Meta Title -->
            <div class="form-group">

              <label>
                Meta Title
              </label>

              <input
                v-model="form.lession_meta_title"
                type="text"
                placeholder="Enter SEO meta title"
              />

            </div>

            <!-- Duration -->
            <div class="form-group">

              <label>
                Duration (Minutes)
              </label>

              <input
                v-model="form.lession_duration"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 45"
              />

            </div>

            <!-- Keywords -->
            <div class="form-group full">

              <label>
                Keywords
              </label>

              <input
                v-model="form.lession_keyword"
                type="text"
                placeholder="lecture, learning, course"
              />

            </div>

            <!-- Video -->
            <div class="form-group full">

              <label>
                Lecture Video
              </label>

              <input
                v-model="form.lession_video"
                type="text"
                placeholder="Enter video URL"
              />

            </div>

            <!-- Description -->
            <div class="form-group full">

              <label>
                Description
              </label>

              <textarea
                v-model="form.lession_desc"
                rows="5"
                placeholder="Enter lecture description"
              ></textarea>

            </div>

          </div>

        </div>

        <!-- Footer -->
        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="close"
            :disabled="saving"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            @click="save"
            :disabled="saving"
          >

            <i
              v-if="!saving"
              class="bx bx-save"
            ></i>

            <i
              v-else
              class="bx bx-loader-alt bx-spin"
            ></i>

            {{
              saving
                ? 'Saving...'
                : (
                    form.lession_id
                      ? 'Update Lecture'
                      : 'Create Lecture'
                  )
            }}

          </button>

        </div>

      </div>

    </div>

    <!-- ========================================================= -->
    <!-- VIEW MODAL -->
    <!-- ========================================================= -->

    <div
      v-if="viewModal"
      class="modal-overlay"
      @click.self="viewModal = false"
    >

      <div class="modal view-modal">

        <!-- Header -->
        <div class="modal-header">

          <div>

            <h3>
              Lecture Details
            </h3>

            <p>
              View lecture information.
            </p>

          </div>

          <button
            class="close-btn"
            @click="viewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <!-- Body -->
        <div
          v-if="selectedLecture"
          class="modal-body"
        >

          <!-- Lecture Preview -->
          <div class="lecture-preview">

            <div class="big-lecture-icon">
              <i class="bx bx-play"></i>
            </div>

            <div>

              <h2>
                {{ selectedLecture.lession_title }}
              </h2>

              <p>

                {{ getCourseName(selectedLecture.lession_parent) }}

                <span>•</span>

                {{ getModuleName(selectedLecture.lession_module) }}

              </p>

            </div>

          </div>

          <!-- Details -->
          <div class="detail-grid">

            <!-- ID -->
            <div class="detail-item">

              <label>
                Lecture ID
              </label>

              <strong>
                #{{ selectedLecture.lession_id }}
              </strong>

            </div>

            <!-- Category -->
            <div class="detail-item">

              <label>
                Category
              </label>

              <strong>
                {{ getCategoryName(selectedLecture.lession_cat) }}
              </strong>

            </div>

            <!-- Duration -->
            <div class="detail-item">

              <label>
                Duration
              </label>

              <strong>

                {{
                  selectedLecture.lession_duration
                    ? selectedLecture.lession_duration + ' min'
                    : '-'
                }}

              </strong>

            </div>

            <!-- Created -->
            <div class="detail-item">

              <label>
                Created
              </label>

              <strong>
                {{ formatDate(selectedLecture.lession_datetime) }}
              </strong>

            </div>

          </div>

          <!-- Description -->
          <div class="description-section">

            <label>
              Description
            </label>

            <p>

              {{
                selectedLecture.lession_desc ||
                'No description available.'
              }}

            </p>

          </div>

          <!-- Video -->
          <div
            v-if="selectedLecture.lession_video"
            class="video-url"
          >

            <label>
              Video URL
            </label>

            <div>

              <i class="bx bx-link"></i>

              {{ selectedLecture.lession_video }}

            </div>

          </div>

        </div>

        <!-- Footer -->
        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="viewModal = false"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="editFromView"
          >

            <i class="bx bx-edit"></i>

            Edit Lecture

          </button>

        </div>

      </div>

    </div>

    <!-- ========================================================= -->
    <!-- DELETE MODAL -->
    <!-- ========================================================= -->

    <div
      v-if="deleteModal"
      class="modal-overlay"
      @click.self="deleteModal = false"
    >

      <div class="delete-modal">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>
          Delete Lecture?
        </h3>

        <p>

          Are you sure you want to delete

          <strong>
            {{ selectedLecture?.lession_title }}
          </strong>?

          This action cannot be undone.

        </p>

        <div class="delete-actions">

          <button
            class="secondary-btn"
            @click="deleteModal = false"
            :disabled="deleting"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            @click="confirmDelete"
            :disabled="deleting"
          >

            <i
              v-if="!deleting"
              class="bx bx-trash"
            ></i>

            <i
              v-else
              class="bx bx-loader-alt bx-spin"
            ></i>

            {{ deleting ? 'Deleting...' : 'Delete Lecture' }}

          </button>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import {
  getLecturesApi,
  getLectureStatsApi,
  getLectureApi,
  createLectureApi,
  updateLectureApi,
  deleteLectureApi,
} from "@/apis/lectureapis";

import { getCoursesApi } from "@/apis/courseapis";
import { getModulesApi } from "@/apis/modulecon";
import { getCategoriesApi } from "@/apis/categoryapis";
// Use your existing category API import if you already have one.
// Example:
// import { getCategoriesApi } from "@/apis/categoryapis";

export default {
  name: "LectureScreen",

  data() {
    return {
      search: "",
      selectedCourse: "",
      selectedModule: "",
      selectedCategory: "",

      page: 0,
      perPage: 10,

      lectures: [],
      courses: [],
      modules: [],
      categories: [],

      totalLectures: 0,
      totalPages: 1,

      lectureCount: 0,
      videoLectureCount: 0,
      textLectureCount: 0,
      newLecturesThisMonth: 0,

      showModal: false,
      viewModal: false,
      deleteModal: false,

      selectedLecture: null,
      form: {},

      loading: false,
      saving: false,
      deleting: false,

      coursesLoading: false,
      modulesLoading: false,
      categoriesLoading: false,
    };
  },

  computed: {
    filteredLectures() {
      return this.lectures;
    },

    filteredModulesForDropdown() {
      if (!this.selectedCourse) {
        return this.modules;
      }

      return this.modules.filter(
        (module) =>
          String(module.module_parent) === String(this.selectedCourse)
      );
    },

    formModules() {
      if (!this.form.lession_parent) {
        return this.modules;
      }

      return this.modules.filter(
        (module) =>
          String(module.module_parent) ===
          String(this.form.lession_parent)
      );
    },
  },

  watch: {
    search() {
      this.resetAndLoad();
    },

    selectedCourse() {
      this.page = 0;

      // Reset module when course changes.
      this.selectedModule = "";

      this.loadLectures();
    },

    selectedModule() {
      this.resetAndLoad();
    },

    selectedCategory() {
      this.resetAndLoad();
    },
  },

  mounted() {
    this.loadCourses();
    this.loadModules();
    this.loadCategories();
    this.loadLectures();
    this.loadStats();
  },

  methods: {
    async loadCategories() {
  this.categoriesLoading = true;

  try {
    const response = await getCategoriesApi({
      page: 1,
      limit: 100,
      search: "",
      cat_type: "course",
    });

    if (response?.status) {
      this.categories = response.categories || [];
    } else {
      this.categories = [];

      console.error(
        response?.success || "Categories fetch failed."
      );
    }
  } catch (error) {
    console.error("Load Categories Error:", error);

    this.categories = [];
  } finally {
    this.categoriesLoading = false;
  }
},
    resetAndLoad() {
      this.page = 0;
      this.loadLectures();
    },

    async loadCourses() {
      this.coursesLoading = true;

      try {
        const response = await getCoursesApi({
          page: 1,
          limit: 100,
          search: "",
          course_typ: "",
          category: "",
          creator_id: "",
        });

        if (response?.status) {
          this.courses = response.courses || [];
        } else {
          this.courses = [];
          console.error(
            response?.success || "Courses fetch failed."
          );
        }
      } catch (error) {
        console.error("Load Courses Error:", error);
        this.courses = [];
      } finally {
        this.coursesLoading = false;
      }
    },

    async loadModules() {
      this.modulesLoading = true;

      try {
        const response = await getModulesApi({
          page: 1,
          limit: 100,
          search: "",
          course_id: "",
          module_type: "",
        });

        if (response?.status) {
          this.modules = response.modules || [];
        } else {
          this.modules = [];
          console.error(
            response?.success || "Modules fetch failed."
          );
        }
      } catch (error) {
        console.error("Load Modules Error:", error);
        this.modules = [];
      } finally {
        this.modulesLoading = false;
      }
    },
    async loadLectures() {
      this.loading = true;

      try {
        const response = await getLecturesApi({
          page: this.page + 1,
          limit: this.perPage,
          search: this.search,
          course_id: this.selectedCourse,
          module_id: this.selectedModule,
          category_id: this.selectedCategory,
        });

        if (response?.status) {
          this.lectures = response.lectures || [];

          this.totalLectures = Number(response.total || 0);

          this.totalPages = Math.max(
            Number(response.totalPages || 1),
            1
          );

          this.page = Math.max(
            Number(response.page || 1) - 1,
            0
          );
        } else {
          this.lectures = [];
          this.totalLectures = 0;
          this.totalPages = 1;

          console.error(
            response?.success || "Lectures fetch failed."
          );
        }
      } catch (error) {
        console.error("Load Lectures Error:", error);

        this.lectures = [];
        this.totalLectures = 0;
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      try {
        const response = await getLectureStatsApi();

        if (response?.status) {
          this.totalLectures = Number(
            response.totalLectures || 0
          );

          this.lectureCount = Number(
            response.lectures || 0
          );

          this.videoLectureCount = Number(
            response.videoLectures || 0
          );

          this.textLectureCount = Number(
            response.textLectures || 0
          );

          this.newLecturesThisMonth = Number(
            response.newLecturesThisMonth || 0
          );
        }
      } catch (error) {
        console.error("Load Lecture Stats Error:", error);
      }
    },

    openForm(lecture = null) {
      if (lecture) {
        this.form = {
          ...lecture,

          lession_module:
            lecture.lession_module || "",

          lession_parent:
            lecture.lession_parent || "",

          lession_cat:
            lecture.lession_cat || "",

          lession_title:
            lecture.lession_title || "",

          lession_meta_title:
            lecture.lession_meta_title || "",

          lession_duration:
            lecture.lession_duration || 0,

          lession_keyword:
            lecture.lession_keyword || "",

          lession_video:
            lecture.lession_video || "",

          lession_desc:
            lecture.lession_desc || "",
        };
      } else {
        this.form = {
          lession_module: "",
          lession_parent: "",
          lession_title: "",
          lession_meta_title: "",
          lession_keyword: "",
          lession_cat: "",
          lession_desc: "",
          lession_duration: 0,
          lession_video: "",
        };
      }

      this.showModal = true;
    },

    close() {
      this.showModal = false;
      this.form = {};
    },

    async viewLecture(lecture) {
      if (!lecture?.lession_id) {
        return;
      }

      try {
        const response = await getLectureApi(
          lecture.lession_id
        );

        if (response?.status) {
          this.selectedLecture = response.lecture;
          this.viewModal = true;
        } else {
          alert(
            response?.success ||
              "Lecture fetch failed."
          );
        }
      } catch (error) {
        console.error("View Lecture Error:", error);
        alert("Unable to load lecture details.");
      }
    },

    editFromView() {
      const lecture = this.selectedLecture;

      this.viewModal = false;

      if (lecture) {
        this.openForm(lecture);
      }
    },

    onFormCourseChange() {
      /*
       * A module belongs to a course.
       * Therefore when course changes, reset the selected module.
       */
      this.form.lession_module = "";
    },

    async save() {
      const title = String(
        this.form.lession_title || ""
      ).trim();

      if (!title) {
        alert("Lecture title is required.");
        return;
      }

      if (!this.form.lession_parent) {
        alert("Please select a course.");
        return;
      }

      if (!this.form.lession_module) {
        alert("Please select a module.");
        return;
      }

      const isEdit = !!this.form.lession_id;

      this.saving = true;

      try {
        let response;

        if (isEdit) {
          response = await updateLectureApi(
            this.form
          );
        } else {
          response = await createLectureApi(
            this.form
          );
        }

        if (response?.status) {
          this.close();

          await this.loadLectures();
          await this.loadStats();

          alert(
            response.success ||
              (isEdit
                ? "Lecture updated successfully."
                : "Lecture created successfully.")
          );
        } else {
          alert(
            response?.success ||
              "Lecture save failed."
          );
        }
      } catch (error) {
        console.error(
          "Save Lecture Error:",
          error
        );

        alert("Unable to save lecture.");
      } finally {
        this.saving = false;
      }
    },

    openDelete(lecture) {
      if (!lecture?.lession_id) {
        return;
      }

      this.selectedLecture = lecture;
      this.deleteModal = true;
    },

    async confirmDelete() {
      if (!this.selectedLecture?.lession_id) {
        return;
      }

      this.deleting = true;

      try {
        const response = await deleteLectureApi(
          this.selectedLecture.lession_id
        );

        if (response?.status) {
          this.deleteModal = false;
          this.selectedLecture = null;

          if (
            this.lectures.length === 1 &&
            this.page > 0
          ) {
            this.page--;
          }

          await this.loadLectures();
          await this.loadStats();

          alert(
            response.success ||
              "Lecture deleted successfully."
          );
        } else {
          alert(
            response?.success ||
              "Lecture deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Lecture Error:",
          error
        );

        alert("Unable to delete lecture.");
      } finally {
        this.deleting = false;
      }
    },

    prevPage() {
      if (this.page > 0) {
        this.page--;
        this.loadLectures();
      }
    },

    nextPage() {
      if (this.page < this.totalPages - 1) {
        this.page++;
        this.loadLectures();
      }
    },

    getCourseName(courseId) {
      const course = this.courses.find(
        (item) =>
          String(item.course_id) ===
          String(courseId)
      );

      return course?.title || "-";
    },

    getModuleName(moduleId) {
      const module = this.modules.find(
        (item) =>
          String(item.module_id) ===
          String(moduleId)
      );

      return module?.module_title || "-";
    },

    getCategoryName(categoryId) {
      const category = this.categories.find(
        (item) =>
          String(item.cat_id) ===
          String(categoryId)
      );

      return category?.cat_name || "-";
    },

    formatDate(date) {
      if (!date) {
        return "-";
      }

      return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(date));
    },

    formatDuration(value) {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return "-";
      }

      const duration = Number(value);

      if (Number.isNaN(duration)) {
        return value;
      }

      return `${duration} min`;
    },
  },
};
</script>

<style scoped>
.lecture-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.danger-btn,
.reset-btn {
  border: 0;
  border-radius: 9px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}

.secondary-btn:hover {
  background: #e2e8f0;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Filters */
.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 15px;
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 18px;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 240px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-card select {
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  outline: none;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-card select {
  min-width: 160px;
  padding: 0 35px 0 12px;
}

.search-box input:focus,
.filter-card select:focus,
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #2563eb;
}

.reset-btn {
  height: 42px;
  background: #f1f5f9;
  color: #475569;
}

/* Table */
.table-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-top {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.table-top h3 {
  margin: 0 0 4px;
  font-size: 16px;
}

.table-top span {
  font-size: 13px;
  color: #64748b;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 13px 16px;
  text-align: left;
  white-space: nowrap;
}

td {
  padding: 15px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

tr:hover td {
  background: #fafcff;
}

.action-col {
  text-align: right;
}

.lecture-info {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 230px;
}

.lecture-icon {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.lecture-info strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lecture-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.course-name {
  color: #334155;
  font-weight: 500;
}

.category-badge {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
}

.duration-label {
  color: #94a3b8;
  font-size: 11px;
}

.video-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #16a34a;
  font-size: 12px;
  font-weight: 600;
}

.no-video {
  color: #94a3b8;
  font-size: 12px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.icon-btn.view {
  color: #475569;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  padding: 55px 20px;
  text-align: center;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h4 {
  margin: 10px 0 5px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 13px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 34px;
  height: 34px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 7px;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 760px;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 7px;
  background: #f1f5f9;
  color: #475569;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 17px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label,
.detail-item label,
.description-section label,
.video-url label {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 11px 12px;
  color: #334155;
  font-size: 14px;
  outline: none;
  background: #fff;
}

.form-group textarea {
  resize: vertical;
  min-height: 110px;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* View */
.lecture-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e2e8f0;
}

.big-lecture-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 27px;
}

.lecture-preview h2 {
  margin: 0 0 5px;
  font-size: 19px;
}

.lecture-preview p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.lecture-preview p span {
  margin: 0 6px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 22px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 9px;
}

.detail-item label {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  color: #0f172a;
  font-size: 13px;
}

.description-section {
  margin-top: 22px;
}

.description-section label {
  display: block;
  margin-bottom: 8px;
}

.description-section p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.7;
}

.video-url {
  margin-top: 20px;
}

.video-url label {
  display: block;
  margin-bottom: 8px;
}

.video-url div {
  padding: 11px 13px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #2563eb;
  font-size: 13px;
  word-break: break-all;
}

/* Delete */
.delete-modal {
  width: 420px;
  max-width: 100%;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
}

.delete-modal h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.delete-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
}

/* Responsive */
@media (max-width: 900px) {
  .filter-card {
    flex-wrap: wrap;
  }

  .search-box {
    flex-basis: 100%;
  }

  .filter-card select {
    flex: 1;
  }
}

@media (max-width: 650px) {
  .lecture-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .page-header .primary-btn {
    padding: 9px 12px;
  }

  .page-header .primary-btn i {
    display: none;
  }

  .filter-card {
    display: block;
  }

  .search-box {
    margin-bottom: 10px;
  }

  .filter-card select {
    width: 100%;
    margin-bottom: 10px;
  }

  .reset-btn {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>